import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { cardSurface } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import { computeAllocationSummary } from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface Props {
    locationCages: OverviewCage[];
    selectedSources: WizardSource[];
    initialFishGroupId: number;
    destinations: WizardDestination[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Merge mode — Step 1. The operator picks at least one **additional**
 * source to combine with the initial fish group. The initial source is
 * always selected and cannot be toggled off (locked check + "Initial"
 * pill); the operator must add ≥1 more for the merge to make sense.
 */
export default function MergeSourcesStep(props: Props) {
    const {
        locationCages,
        selectedSources,
        initialFishGroupId,
        destinations,
        populationById,
        dispatch,
    } = props;
    const { t } = useTranslation('fishGroups');

    const allocationSummary = computeAllocationSummary({
        sources: selectedSources,
        destinations,
        populationById,
    });

    const candidates = locationCages
        .filter((cage) => cage.activeFishGroup !== null)
        .map((cage) => ({
            fishGroupId: cage.activeFishGroup?.id ?? 0,
            name: cage.activeFishGroup?.name ?? '',
            cageId: cage.cageId,
            cageName: cage.cageName,
        }));

    const selectedIds = new Set(selectedSources.map((s) => s.fishGroupId));

    return (
        <section>
            <h4 className={styles.heading}>{t('actions.operate.merge.sources.heading')}</h4>
            <p className={styles.description}>{t('actions.operate.merge.sources.description')}</p>

            <AllocationSummary
                summary={allocationSummary}
                sources={selectedSources}
                populationById={populationById}
                titleKey="actions.operate.allocation.combinedSourcesTitle"
            />

            {candidates.length <= 1 ? (
                <EmptyState title={t('actions.operate.merge.sources.empty')} appearance="quiet" />
            ) : (
                <ul className={styles.sourcesList}>
                    {candidates.map((candidate) => {
                        const isSelected = selectedIds.has(candidate.fishGroupId);
                        const isInitial = candidate.fishGroupId === initialFishGroupId;
                        return (
                            <li
                                key={candidate.fishGroupId}
                                {...cardSurface(
                                    { radius: 'sm', padding: 'none', interactive: true },
                                    styles.sourceRow
                                )}
                                onClick={() => {
                                    if (isInitial) return;
                                    dispatch({
                                        type: 'toggleSource',
                                        source: {
                                            fishGroupId: candidate.fishGroupId,
                                            name: candidate.name,
                                            currentCageId: candidate.cageId,
                                        },
                                    });
                                }}>
                                <Checkbox
                                    checked={isSelected}
                                    disabled={isInitial}
                                    onCheckedChange={() => undefined}
                                />
                                <span className={styles.sourceLabel}>
                                    {t('actions.operate.sources.candidateLabel', {
                                        name: candidate.name,
                                        cageName: candidate.cageName,
                                    })}
                                </span>
                                {isInitial && (
                                    <Badge tone="accent" shape="tag">
                                        {t('actions.operate.sources.initialPill')}
                                    </Badge>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
